import React, { useState, useEffect, useCallback } from 'react';
import PageStrip from './PageStrip';
import PagePreview from './PagePreview';
import StudioSidebar from './StudioSidebar';
import usePageOperations from './hooks/usePageOperations';

/**
 * The editor half of Document Studio.
 *
 * It is mounted only once the page count is known (see index.jsx), so
 * usePageOperations seeds the page model exactly once, from a real count. That
 * ordering is the whole point of the split: the model cannot be re-seeded
 * later, so a user's pending cuts can never be discarded by a late-arriving
 * count.
 */
const StudioEditor = ({
  documentName,
  pageCount,
  aiSplitSuggestions = [],
  renderThumbnail,
  renderPreview,
  onApply,
  onCancel,
}) => {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [applying, setApplying] = useState(false);

  const {
    pages,
    splits,
    selectedPageIndex,
    rotatePage,
    deletePage,
    undeletePage,
    toggleSplit,
    reorderPages,
    setSelectedPage,
    annotatePage,
    undo,
    redo,
    canUndo,
    canRedo,
    buildOperations,
    isDirty,
  } = usePageOperations(pageCount);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (selectedPageIndex > 0) {
          setSelectedPage(selectedPageIndex - 1);
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (selectedPageIndex < pages.length - 1) {
          setSelectedPage(selectedPageIndex + 1);
        }
      } else if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        if (selectedPageIndex > 0) {
          toggleSplit(selectedPageIndex);
        }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const page = pages[selectedPageIndex];
        if (page && !page.deleted) {
          deletePage(selectedPageIndex);
        }
      } else if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        rotatePage(selectedPageIndex);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancelClick();
      } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleApply();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPageIndex, pages, undo, redo, toggleSplit, deletePage, rotatePage, setSelectedPage]);

  const handleCancelClick = () => {
    if (isDirty) {
      setShowCancelConfirm(true);
    } else {
      onCancel();
    }
  };

  const handleConfirmCancel = () => {
    onCancel();
  };

  const handleApply = async () => {
    setApplying(true);
    const operations = buildOperations();
    try {
      await onApply(operations);
    } catch (err) {
      console.error('[DocumentStudio] Apply failed:', err);
    } finally {
      setApplying(false);
    }
  };

  const handleAcceptAllSuggestions = () => {
    aiSplitSuggestions.forEach((suggestion) => {
      if (!splits.has(suggestion.beforePageIndex)) {
        toggleSplit(suggestion.beforePageIndex);
      }
    });
  };

  return (
    <div className="ds-overlay">
      <div className="ds-container">
        <div className="ds-topbar">
          <button className="ds-back-btn" onClick={handleCancelClick}>
            ← Back
          </button>
          <div className="ds-topbar-divider" />
          <span className="ds-topbar-title">Document Studio</span>
          <span className="ds-topbar-document">{documentName}</span>
          <div className="ds-topbar-spacer" />
          <button
            className="ds-btn-secondary"
            onClick={handleCancelClick}
            disabled={applying}
          >
            Cancel
          </button>
          <button
            className="ds-btn-primary"
            onClick={handleApply}
            disabled={applying || !isDirty}
          >
            {applying ? 'Applying...' : 'Apply Changes'}
          </button>
        </div>

        <div className="ds-main">
          {/* Left: Vertical thumbnail strip */}
          <div className="ds-thumbnail-rail">
            <PageStrip
              pages={pages}
              splits={splits}
              selectedPageIndex={selectedPageIndex}
              aiSuggestions={aiSplitSuggestions}
              renderThumbnail={renderThumbnail}
              onSelectPage={setSelectedPage}
              onReorderPages={reorderPages}
              onToggleSplit={toggleSplit}
              vertical={true}
            />
          </div>

          {/* Center: Full page preview */}
          <div className="ds-preview-container">
            <PagePreview
              page={pages[selectedPageIndex]}
              pageIndex={selectedPageIndex}
              renderPreview={renderPreview}
            />
          </div>

          {/* Right: Compact controls panel */}
          <div className="ds-controls-rail">
            <div className="ds-controls-section">
              <div className="ds-controls-label">PAGE {selectedPageIndex + 1} OF {pages.length}</div>
            </div>

            <div className="ds-controls-section">
              <div className="ds-controls-label">ACTIONS</div>
              <button
                className="ds-control-btn"
                onClick={() => selectedPageIndex > 0 && toggleSplit(selectedPageIndex)}
                disabled={selectedPageIndex === 0}
                title="Split before this page (S)"
              >
                ✂️ Split Here
              </button>
              <button
                className="ds-control-btn"
                onClick={() => {
                  const page = pages[selectedPageIndex];
                  page?.deleted ? undeletePage(selectedPageIndex) : deletePage(selectedPageIndex);
                }}
                title="Delete page (⌫)"
              >
                {pages[selectedPageIndex]?.deleted ? '↩️ Restore' : '🗑 Delete Page'}
              </button>
              <button
                className="ds-control-btn"
                onClick={() => rotatePage(selectedPageIndex)}
                title="Rotate 90° (R)"
              >
                ↻ Rotate 90°
              </button>
              <button
                className="ds-control-btn"
                onClick={() => annotatePage(selectedPageIndex, pages[selectedPageIndex]?.annotation ? null : 'cover_sheet')}
                title="Mark as cover sheet"
              >
                🏷 Annotate
              </button>
            </div>

            <div className="ds-controls-section">
              <div className="ds-controls-label">HISTORY</div>
              <button className="ds-control-btn" onClick={undo} disabled={!canUndo} title="Undo (⌘Z)">
                ↩ Undo
              </button>
              <button className="ds-control-btn" onClick={redo} disabled={!canRedo} title="Redo (⌘⇧Z)">
                ↪ Redo
              </button>
            </div>

            {aiSplitSuggestions.length > 0 && (
              <div className="ds-controls-section">
                <div className="ds-controls-label">AI SUGGESTIONS</div>
                {aiSplitSuggestions.map((s, i) => (
                  <button
                    key={i}
                    className={`ds-control-btn ds-control-btn--ai ${splits.has(s.beforePageIndex) ? 'ds-control-btn--active' : ''}`}
                    onClick={() => toggleSplit(s.beforePageIndex)}
                    title={s.reason || 'AI suggested split'}
                  >
                    ◆ Split at pg {s.beforePageIndex + 1}
                  </button>
                ))}
                <button className="ds-control-btn ds-control-btn--ai-accept" onClick={handleAcceptAllSuggestions}>
                  ✓ Accept All
                </button>
              </div>
            )}

            <div className="ds-controls-section ds-controls-section--pending">
              <div className="ds-controls-label">PENDING</div>
              <StudioSidebar
                pages={pages}
                splits={splits}
                selectedPageIndex={selectedPageIndex}
                aiSuggestions={[]}
                onSplitAtSelected={() => {}}
                onDeletePage={deletePage}
                onRotatePage={rotatePage}
                onAcceptAllSuggestions={() => {}}
                onUndo={undo}
                canUndo={canUndo}
                pendingOnly={true}
              />
            </div>

            <div className="ds-controls-section">
              <div className="ds-controls-label">SHORTCUTS</div>
              <div className="ds-shortcuts">
                <span>S — Split</span>
                <span>⌫ — Delete</span>
                <span>R — Rotate</span>
                <span>⌘Z — Undo</span>
                <span>← → — Navigate</span>
              </div>
            </div>
          </div>
        </div>

        {showCancelConfirm && (
          <div className="ds-modal-overlay" onClick={() => setShowCancelConfirm(false)}>
            <div className="ds-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ds-modal-header">Discard Changes?</div>
              <div className="ds-modal-body">
                You have pending changes that will be lost if you cancel.
              </div>
              <div className="ds-modal-actions">
                <button
                  className="ds-btn-secondary"
                  onClick={() => setShowCancelConfirm(false)}
                >
                  Keep Editing
                </button>
                <button
                  className="ds-btn-danger"
                  onClick={handleConfirmCancel}
                >
                  Discard Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudioEditor;
